import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  IconButton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Chip,
  Tooltip,
  Typography,
  Avatar,
  AvatarGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import useHttp from "../../Hooks/use-http";
import { useHistory } from "react-router-dom";
import Loading from "../../Loading/Loading";
import SearchFilter from "../CommonComponent/SearchFilter";
import CommonTablePagination from "../CommonComponent/CommonTablePagination";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import { PAGINATIONBASEVALUE } from "../../Constant";
import PortalTitle from "../CommonComponent/PortalTitle";

// Updated headers structure for grouped notifications
const headers = [
  { label: "Notification ID", key: "notificationID" },
  { label: "Title", key: "title" },
  { label: "Message", key: "message" },
  { label: "App", key: "appName" },
  { label: "Recipients", key: "recipients" },
  { label: "Status", key: "status" },
  { label: "Sent At", key: "time" },
  { label: "Actions", key: "actions" },
];

const headerWidths = {
  "Notification ID": "15%",
  Title: "15%",
  Message: "20%",
  App: "10%",
  Recipients: "15%",
  Status: "10%",
  "Sent At": "10%",
  Actions: "5%",
};

// Helper function for stable sorting
function stableSort(array, comparator) {
  if (!array) return [];
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  const valueA = getOrderValue(a, orderBy);
  const valueB = getOrderValue(b, orderBy);

  if (valueB < valueA) return -1;
  if (valueB > valueA) return 1;
  return 0;
}

function getOrderValue(item, key) {
  // Handle date fields specially
  if (key === "time") {
    return new Date(item.time || 0);
  }
  // Handle recipient count
  if (key === "recipients") {
    return item.totalRecipients || 0;
  }
  // Handle status for sorting
  if (key === "status") {
    return `${item.successCount || 0}-${item.failedCount || 0}`;
  }

  const value = item[key] || "";
  return typeof value === "string" ? value.toLowerCase() : value;
}

const Notifications = () => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("time");
  const [notificationData, setNotificationData] = useState([]);
  const { sendRequest, isLoading } = useHttp();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATIONBASEVALUE);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [filteredData, setFilteredData] = useState(null);
  const [search, setSearch] = useState("");
  const [isApiCall, setIsApiCall] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [sendRequest, isApiCall]);

  const fetchNotifications = () => {
    sendRequest(
      {
        url: "/notificationLists",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"), // Adjust based on your auth
        },
      },
      notificationList
    );
  };

  const notificationList = (data) => {
    setNotificationData(data?.notifications || []);
  };

  useEffect(() => {
    const sortedData = stableSort(
      notificationData,
      order === "asc"
        ? (a, b) => ascendingComparator(a, b, orderBy)
        : (a, b) => descendingComparator(a, b, orderBy)
    );

    const paginatedData = sortedData?.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    setFilteredData(paginatedData);
  }, [notificationData, order, orderBy, page, rowsPerPage]);

  function ascendingComparator(a, b, orderBy) {
    const valueA = getOrderValue(a, orderBy);
    const valueB = getOrderValue(b, orderBy);

    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
  }

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, notification) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // setSelectedNotification(null);
  };

  const handleViewDetails = (notification) => {
    setSelectedNotification(notification);
    setDetailDialogOpen(true);
    handleMenuClose();
  };

  const handleResend = (notification) => {
    // Prepare the data for pre-filling the form
    const notificationData = {
      title: notification.title,
      message: notification.message,
      appName: notification.appName,
      data: notification.data || "",
      // For custom notifications, include recipient data if available
      ...(notification.appName === "custom" &&
        notification.recipients && {
          corporateID: notification.corporateID || "",
          driverEmailIDs: notification.recipients
            .filter((recipient) => recipient.emailID)
            .map((recipient) => recipient.emailID),
          riderMobileNumbers: notification.recipients
            .filter((recipient) => recipient.mobileNumber)
            .map((recipient) => recipient.mobileNumber),
        }),
    };

    // Navigate to send notification page with the data as state
    history.push({
      pathname: "/send-notification",
      state: { notificationData },
    });

    handleMenuClose();
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedNotification(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getAppNameLabel = (appName) => {
    switch (appName) {
      case "driver":
        return "Driver App";
      case "rider":
        return "Rider App";
      case "web":
        return "Web";
      case "direct":
        return "Direct Token";
      case "all":
        return "All Apps";
      default:
        return appName || "-";
    }
  };

  const getRecipientDisplay = (recipients) => {
    if (!recipients || recipients.length === 0) return "-";

    const firstRecipient = recipients[0];
    const primaryIdentifier =
      firstRecipient.emailID || firstRecipient.mobileNumber;

    if (recipients.length === 1) {
      return primaryIdentifier;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <span>{primaryIdentifier}</span>
        <Tooltip title={`+${recipients.length - 1} more recipients`}>
          <Chip
            label={`+${recipients.length - 1}`}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      </Box>
    );
  };

  const getStatusChip = (notification) => {
    const { successCount, failedCount, totalRecipients } = notification;

    if (successCount === totalRecipients) {
      return <Chip label="All Success" color="success" size="small" />;
    } else if (failedCount === totalRecipients) {
      return <Chip label="All Failed" color="error" size="small" />;
    } else {
      return (
        <Tooltip title={`${successCount} success, ${failedCount} failed`}>
          <Chip label="Partial" color="warning" size="small" />
        </Tooltip>
      );
    }
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            mb: 2,
            borderRadius: "10px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: "none",
            borderBottom: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 1,
              pb: 1,
              borderRadius: "10px",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1, md: 0 },
            }}
          >
            <Box component="h2" sx={{ m: 0, display: "flex", alignItems: "center" }}>
              <PortalTitle fallback="Notifications" />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Button
                onClick={() => history.push("/send-notification")}
                variant="contained"
                size="medium"
                sx={{
                  backgroundColor: "#44B150",
                  ":hover": {
                    backgroundColor: "#44B150",
                    opacity: 0.8,
                  },
                  boxShadow: "none",
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  padding: "8px 20px",
                  textTransform: "none",
                  minWidth: "110px",
                  marginLeft: "12px",
                }}
              >
                Send Notification
              </Button>
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            style={{ width: "100%", margin: "auto", borderRadius: "5px" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {headers?.map((header) => (
                    <TableCell
                      key={header.key}
                      sx={{
                        py: 0,
                        px: 1,
                        fontWeight: "bold",
                        color: "rgba(0, 0, 0, 0.87)",
                        width: headerWidths[header.label],
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === header.key}
                        direction={orderBy === header.key ? order : "asc"}
                        onClick={() => handleSort(header.key)}
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={headers?.length}>
                      <Loading datatable="true" />
                    </TableCell>
                  </TableRow>
                ) : filteredData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headers?.length}>
                      <div style={{ textAlign: "center", marginTop: "10px" }}>
                        No Notifications Available
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontSize: "0.8rem", p: 1 }}>
                        {item.notificationID || "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", p: 1 }}>
                        {item.title || "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", p: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {item.message?.length > 50 ? (
                            <>
                              {item.message.substring(0, 50)}...
                              <Tooltip title={item.message}>
                                <InfoIcon
                                  sx={{
                                    fontSize: 16,
                                    ml: 0.5,
                                    color: "text.secondary",
                                  }}
                                />
                              </Tooltip>
                            </>
                          ) : (
                            item.message || "-"
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", p: 1 }}>
                        {getAppNameLabel(item.appName)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", p: 1 }}>
                        <Tooltip
                          title={
                            <Box>
                              <strong>
                                Recipients ({item.totalRecipients}):
                              </strong>
                              {item.recipients
                                ?.slice(0, 10)
                                .map((recipient, idx) => (
                                  <div key={idx}>
                                    {recipient.emailID ||
                                      recipient.mobileNumber}
                                    {recipient.corporateID &&
                                      ` (${recipient.corporateID})`}
                                  </div>
                                ))}
                              {item.recipients?.length > 10 && (
                                <div>
                                  ...and {item.recipients.length - 10} more
                                </div>
                              )}
                            </Box>
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AvatarGroup max={3}>
                              {item.recipients
                                ?.slice(0, 3)
                                .map((recipient, idx) => (
                                  <Avatar
                                    key={idx}
                                    sx={{ width: 24, height: 24 }}
                                  >
                                    <PersonIcon sx={{ fontSize: 14 }} />
                                  </Avatar>
                                ))}
                            </AvatarGroup>
                            <span>{item.totalRecipients} recipients</span>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", p: 1 }}>
                        {getStatusChip(item)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", p: 1 }}>
                        {formatDate(item.time)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => handleMenuClick(event, item)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              background: "white",
              zIndex: 1,
              border: "none !important",
              borderTop: "none !important",
              borderBottom: "none !important",
            }}
          >
            <CommonTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              setPage={setPage}
              filteredData={notificationData}
              styling={{
                display: "flex",
                justifyContent: "flex-end",
                border: "none",
                borderTop: "none",
                borderBottom: "none",
                "& .MuiTablePagination-root": {
                  border: "none",
                  borderTop: "none",
                  borderBottom: "none",
                },
                "& .MuiTablePagination-toolbar": {
                  border: "none",
                  borderTop: "none",
                  borderBottom: "none",
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Menu for actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #ccc",
          },
        }}
      >
        <MenuItem onClick={() => handleViewDetails(selectedNotification)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {/* Resend option */}
        <MenuItem onClick={() => handleResend(selectedNotification)}>
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Resend</ListItemText>
        </MenuItem>
      </Menu>

      {/* Detail Dialog */}
      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div">
            Notification Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ID: {selectedNotification?.notificationID}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box sx={{ mt: 1 }}>
              {/* Title and Message Section */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Title:
                </Typography>
                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    p: 1.5,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  {selectedNotification.title || "No title provided"}
                </Typography>

                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "bold", mt: 2 }}
                >
                  Message:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    p: 1.5,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {selectedNotification.message || "No message content"}
                </Typography>
              </Box>

              {/* Metadata in a grid layout */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    App Type:
                  </Typography>
                  <Typography variant="body2">
                    {getAppNameLabel(selectedNotification.appName)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Sent By:
                  </Typography>
                  <Typography variant="body2">
                    {selectedNotification.createdBy || "Unknown"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Sent At:
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedNotification.time)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Status:
                  </Typography>
                  <Box>
                    {getStatusChip(selectedNotification)}
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      {selectedNotification.successCount || 0} successful,{" "}
                      {selectedNotification.failedCount || 0} failed
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Total Recipients:
                  </Typography>
                  <Typography variant="body2">
                    {selectedNotification.totalRecipients || 0}
                  </Typography>
                </Grid>
              </Grid>

              {/* Recipients Section */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  pb: 1,
                  mb: 2,
                }}
              >
                Recipients ({selectedNotification.totalRecipients || 0})
              </Typography>

              {selectedNotification.recipients &&
              selectedNotification.recipients.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                  {selectedNotification.recipients.map((recipient, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        border: "1px solid",
                        borderColor:
                          recipient.status === "success"
                            ? "success.light"
                            : "error.light",
                        bgcolor:
                          recipient.status === "success"
                            ? "success.light"
                            : "error.light",
                        "&:hover": {
                          bgcolor:
                            recipient.status === "success"
                              ? "success.light"
                              : "error.light",
                        },
                      }}
                      variant="outlined"
                    >
                      <Grid container spacing={1}>
                        {recipient.emailID && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium" }}
                            >
                              Email:
                            </Typography>
                            <Typography variant="body2">
                              {recipient.emailID}
                            </Typography>
                          </Grid>
                        )}

                        {recipient.mobileNumber && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium" }}
                            >
                              Mobile:
                            </Typography>
                            <Typography variant="body2">
                              {recipient.mobileNumber}
                            </Typography>
                          </Grid>
                        )}

                        {recipient.corporateID && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium" }}
                            >
                              Corporate ID:
                            </Typography>
                            <Typography variant="body2">
                              {recipient.corporateID}
                            </Typography>
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            Status:
                          </Typography>
                          <Chip
                            label={recipient.status || "unknown"}
                            size="small"
                            color={
                              recipient.status === "success"
                                ? "success"
                                : recipient.status === "failed"
                                ? "error"
                                : "default"
                            }
                          />
                        </Grid>

                        {recipient.createdAt && (
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Received: {formatDate(recipient.createdAt)}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  No recipients found for this notification.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDetailDialog}
            variant="contained"
            sx={{ borderRadius: 1 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default Notifications;
